import { useToasterStoreActions } from "@stores:global";
import { useCallback, useEffect, useRef, useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

const ALLOWED_LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
];

interface UseLogoUploadReturn {
  logoPreview: string | null;
  setLogoPreview: (preview: string | null) => void;
  handleLogoUpload: (file: File) => void;
  handleRemoveLogo: () => void;
}

export const useLogoUpload = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>,
  initialPreview?: string | null,
): UseLogoUploadReturn => {
  const { t } = useTranslation();
  const toastActions = useToasterStoreActions();
  const logoReaderRef = useRef<FileReader | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    initialPreview ?? null,
  );

  useEffect(() => {
    return () => {
      if (logoReaderRef.current?.readyState === 1) {
        logoReaderRef.current.abort();
      }
    };
  }, []);

  const handleLogoUpload = useCallback(
    (file: File) => {
      // Validate file type
      if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.logoTypeError"),
          variant: "error",
        });
        return;
      }

      // Validate file size
      if (file.size > 500 * 1024) {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.logoSizeError"),
          variant: "error",
        });
        return;
      }

      const reader = new FileReader();
      logoReaderRef.current = reader;
      reader.onload = () => {
        const base64 = reader.result as string;
        setLogoPreview(base64);
        setValue("logoBase64", base64, { shouldDirty: true });
      };
      reader.onerror = () => {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.fileReadError"),
          variant: "error",
        });
      };
      reader.readAsDataURL(file);
    },
    [setValue, toastActions, t],
  );

  const handleRemoveLogo = useCallback(() => {
    setLogoPreview(null);
    setValue("logoBase64", "", { shouldDirty: true });
  }, [setValue]);

  return {
    logoPreview,
    setLogoPreview,
    handleLogoUpload,
    handleRemoveLogo,
  };
};
